/**
 * Marketing Content - Single Source of Truth
 *
 * All marketing copy lives here. This makes it easy to:
 * - Keep messaging consistent across components
 * - Update copy without touching component code
 * - A/B test different versions
 */

export const meta = {
  title: 'Ordo Golf | The Operating System for Indoor Golf',
  description: 'Run your entire facility from one dashboard. Bookings, lessons, members, and marketing—connected.',
  ogTitle: 'Ordo Golf — Mission Control for Indoor Golf',
  ogDescription: 'The operating system that connects bookings, billing, and member marketing in one premium dashboard.',
} as const

export const nav = {
  cta: 'Book a Demo',
  login: 'Log in',
} as const

export const hero = {
  headline: {
    line1: 'The Only Booking Platform',
    line2: 'With Built-In Email Marketing',
  },
  subheadline: 'Stop juggling 5 tools. Booking, payments, and newsletters — all in one place. Flat $199/mo. No per-booking fees.',
  cta: {
    primary: 'Book a Demo',
    secondary: 'See How It Works',
  },
  // Pills below hero
  capabilities: [
    'Bay booking',
    'Lesson scheduling',
    'Member CRM',
    'Email marketing',
    'Payments',
  ],
} as const

export const trustBar = {
  items: [
    { label: 'White-glove onboarding', icon: 'handshake' },
    { label: 'Member import', icon: 'upload' },
    { label: 'Custom domain', icon: 'globe' },
    { label: 'Stripe + email setup', icon: 'setup' },
  ],
} as const

export const whySwitch = {
  badge: 'Migration Made Easy',
  headline: 'Switching platforms is painful.',
  headlineAccent: "So we made it not.",
  subheadline: "You've got members, bookings, and a system that sort of works. We get it. That's why we handle the heavy lifting.",
  steps: [
    {
      number: '01',
      title: 'Connect your tools',
      description: 'We guide you through Stripe and email setup. Takes 15 minutes with our support.',
    },
    {
      number: '02',
      title: 'Import everything',
      description: 'Members, bays, pricing—uploaded via CSV or we do it for you. Nothing gets lost.',
    },
    {
      number: '03',
      title: 'Launch your portal',
      description: 'Your branded member portal goes live. Custom domain optional but recommended.',
    },
  ],
  checklist: {
    title: 'Go-Live Checklist',
    items: [
      'Stripe account connected',
      'Member list imported',
      'Bays configured',
      'Booking rules set',
      'Welcome email sent',
    ],
  },
  cta: 'Talk to Us About Migration',
} as const

export const operations = {
  badge: 'Built for Operations',
  headline: 'One dashboard.',
  headlineAccent: 'Every workflow.',
  subheadline: 'Stop patching together booking tools, spreadsheets, and email platforms. Ordo connects the workflows that actually run your facility.',
  modules: [
    {
      id: 'bookings',
      title: 'Bookings & Bays',
      description: 'Members book 24/7. Real-time availability. Automatic confirmations and reminders that cut no-shows.',
      features: [
        'Online booking portal',
        'Recurring reservations',
        'Blackout dates',
        'Waitlist management',
        'Automatic reminders',
      ],
    },
    {
      id: 'members',
      title: 'Members & CRM',
      description: 'Every booking builds your member database. Track preferences, history, and engagement—then act on it.',
      features: [
        'Full member profiles',
        'Booking history',
        'Invite-only access',
        'Role-based permissions',
        'Bulk import/export',
      ],
    },
    {
      id: 'marketing',
      title: 'Marketing & Automation',
      description: 'Built-in newsletters, announcements, and automated emails. No Mailchimp subscription required.',
      features: [
        'Newsletter builder',
        'Booking confirmations',
        'Reminder sequences',
        'Promotional campaigns',
        'Engagement tracking',
      ],
    },
    {
      id: 'payments',
      title: 'Payments & Billing',
      description: 'Collect at booking. Manage memberships. See revenue in real-time. Powered by Stripe.',
      features: [
        'Pay-at-booking',
        'Membership billing',
        'Initiation fees',
        'Revenue dashboard',
        'Refund management',
      ],
    },
    {
      id: 'analytics',
      title: 'Analytics & Utilization',
      description: 'See which bays fill, when members book, and where revenue comes from. Data you can act on.',
      features: [
        'Utilization reports',
        'Revenue breakdown',
        'Member activity',
        'Peak hours analysis',
        'Export to CSV',
      ],
    },
  ],
} as const

export const lessons = {
  badge: 'Pro Lessons',
  headline: 'Turn your pros into profit centers',
  description: 'Built-in lesson booking that integrates with your bays. Instructors manage their own schedules. Members book lessons alongside bay time. Revenue stays in-house.',
  features: [
    'Instructor profiles & rates',
    'Availability management',
    'Bay + lesson in one booking',
    'Automatic bay allocation',
    'Enable when you\'re ready',
  ],
  cta: 'See lesson booking in action',
} as const

export const branding = {
  badge: 'White-Label',
  headline: 'Your brand, not ours',
  description: "Members see your club—not a third-party tool. Custom logo, colors, and domain. Looks like a custom build that would cost 10x more.",
  features: [
    'Upload your logo',
    'Set brand colors',
    'Custom subdomain or your own domain',
    'Branded emails',
    'No Ordo branding visible to members',
  ],
  example: {
    name: 'Boulevard Golf Club',
    domain: 'members.blvdgolf.com',
  },
} as const

export const pricing = {
  badge: 'Simple Pricing',
  headline: '$199',
  period: '/month',
  commitment: '12-month commitment',
  subheadline: 'Everything included. No tiers. No per-member fees. No nickel-and-diming.',
  included: [
    'Unlimited bays',
    'Unlimited members',
    'Built-in email marketing',
    'Payment processing',
    'Real-time analytics',
    'Lesson booking (optional)',
    'Custom branding',
    'Priority support',
  ],
  replaces: {
    headline: "What you're replacing",
    items: [
      { name: 'Booking software', cost: '$50-150/mo' },
      { name: 'Email marketing', cost: '$30-100/mo' },
      { name: 'CRM/spreadsheets', cost: '$0-50/mo' },
      { name: 'Custom development', cost: '$$$' },
    ],
  },
  cta: {
    primary: 'Book a Demo',
    secondary: 'Talk Migration',
  },
} as const

export const finalCta = {
  headline: 'Ready to run your facility on Ordo?',
  subheadline: 'Book a 20-minute demo. We\'ll show you exactly how your club would look and work on our platform.',
  cta: 'Book a Demo',
  reassurance: 'Free demo • No commitment • See your branded portal',
} as const

export const footer = {
  tagline: 'The operating system for indoor golf.',
  links: {
    product: [
      { label: 'Features', href: '#features' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Demo', href: '#demo' },
    ],
    company: [
      { label: 'Support', href: '/support' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
    ],
  },
  copyright: `© ${new Date().getFullYear()} Ordo Golf`,
  builtIn: 'Built in Nashville',
} as const
